import React, { useEffect, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { motion } from "framer-motion";
import { NavLink } from "react-router-dom";
import { FaArrowUpRightFromSquare } from "react-icons/fa6";
import "./featured.css";

const REACT_APP_API_URL = process.env.REACT_APP_API_URL;

/* ------------------ ANIMATIONS ------------------ */

const containerVariants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.12,
    },
  },
};

const cardVariants = {
  hidden: {
    opacity: 0,
    y: 35,
    scale: 0.96,
  },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 160,
      damping: 20,
    },
  },
};

const imageVariants = {
  rest: {
    scale: 1,
  },
  hover: {
    scale: 1.09,
    transition: {
      duration: 0.5,
      ease: "easeOut",
    },
  },
};

/* ------------------ PRICE HELPER ------------------ */

const formatPrice = (price) => {
  const numericPrice = Number(price);

  if (!Number.isFinite(numericPrice)) return null;

  return `₹${numericPrice.toLocaleString("en-IN")}`;
};

/* ------------------ COMPONENT ------------------ */

const Featured = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);

        const res = await fetch(`${REACT_APP_API_URL}/products`);

        if (!res.ok) {
          throw new Error("Failed to fetch products");
        }

        const data = await res.json();

        if (!Array.isArray(data)) {
          throw new Error("Invalid products response");
        }

        // Featured categories
        const featuredCategories = ["POP Phone", "iPhone", "iPad", "MacBook"];

        const featuredProducts = [];

        // Get one product from each featured category
        featuredCategories.forEach((category) => {
          const categoryProduct = data.find((product) => {
            if (product.category === category) {
              if (category === "iPad" || category === "MacBook") {
                return product.variants && product.variants.length > 0;
              }

              return true;
            }

            return false;
          });

          if (categoryProduct) {
            featuredProducts.push(categoryProduct);
          }
        });

        // Add Accessories if fewer than 6 products
        if (featuredProducts.length < 6) {
          const accessoriesProducts = data
            .filter(
              (product) =>
                product.category === "Accessories" &&
                (product.price ||
                  (product.variants && product.variants.length > 0)),
            )
            .slice(0, 6 - featuredProducts.length);

          featuredProducts.push(...accessoriesProducts);
        }

        setProducts(featuredProducts.slice(0, 6));
      } catch (err) {
        console.error("Failed to fetch featured products", err);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  /* ------------------ PRICE FUNCTION ------------------ */

  const getProductPrice = (product) => {
    if (
      product.price !== undefined &&
      product.price !== null &&
      product.price !== ""
    ) {
      return formatPrice(product.price) || "Price on request";
    }

    if (product.variants && product.variants.length > 0) {
      const prices = product.variants
        .map((variant) => Number(variant.price))
        .filter((price) => Number.isFinite(price) && price > 0);

      if (prices.length > 0) {
        const minPrice = Math.min(...prices);
        const maxPrice = Math.max(...prices);

        if (minPrice === maxPrice) {
          return formatPrice(minPrice);
        }

        return `${formatPrice(minPrice)} - ${formatPrice(maxPrice)}`;
      }
    }

    return "Price on request";
  };

  /* ------------------ LOADING ------------------ */

  if (loading) {
    return (
      <section className="featured-section">
        <Container>
          <div className="featured-header">
            <span className="featured-eyebrow">HANDPICKED FOR YOU</span>
            <h2>
              Featured <strong>Products.</strong>
            </h2>
            <p>Discover our selection of premium Apple products.</p>
          </div>

          <div className="featured-loading">
            <div className="featured-loader"></div>
            <p>Loading featured products...</p>
          </div>
        </Container>
      </section>
    );
  }

  /* ------------------ MAIN UI ------------------ */

  return (
    <section className="featured-section">
      <Container fluid>
        {/* HEADER */}

        <motion.div
          className="featured-header"
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="featured-eyebrow">HANDPICKED FOR YOU</span>

          <h2>
            Featured <strong>Products.</strong>
          </h2>

          <p>Discover our selection of premium Apple products.</p>
        </motion.div>

        {/* PRODUCTS */}

        {products.length > 0 ? (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
          >
            <Row className="featured-product-row">
              {products.map((product, index) => {
                const productId = product._id?.$oid || product._id;

                const firstImage = Array.isArray(product.images)
                  ? product.images[0] || "/placeholder.jpg"
                  : typeof product.images === "object" &&
                      product.images !== null
                    ? product.images[Object.keys(product.images)[0]]?.[0] ||
                      "/placeholder.jpg"
                    : "/placeholder.jpg";

                return (
                  <Col
                    lg={2}
                    md={4}
                    sm={6}
                    xs={6}
                    key={productId || index}
                    className="featured-product-col"
                  >
                    <NavLink
                      to={`/product-details/${productId}`}
                      className="featured-product-link"
                    >
                      <motion.div
                        className="featured-product-card"
                        variants={cardVariants}
                        initial="rest"
                        whileHover="hover"
                      >
                        {/* CARD TOP */}

                        <div className="featured-card-top">
                          <span className="featured-product-number">
                            {String(index + 1).padStart(2, "0")}
                          </span>

                          <span className="featured-product-arrow">
                            <FaArrowUpRightFromSquare />
                          </span>
                        </div>

                        {/* PRODUCT IMAGE */}

                        <motion.div
                          className="featured-image-wrapper"
                          variants={imageVariants}
                        >
                          <img
                            src={firstImage}
                            alt={product.name || "Featured product"}
                            loading="lazy"
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = "/placeholder.jpg";
                            }}
                          />
                        </motion.div>

                        {/* PRODUCT DETAILS */}

                        <div className="featured-product-details">
                          <h3 title={product.name}>{product.name}</h3>

                          <div className="featured-price">
                            {getProductPrice(product)}
                          </div>

                          <span className="featured-view-text">
                           Buy Now
                          </span>
                        </div>
                      </motion.div>
                    </NavLink>
                  </Col>
                );
              })}
            </Row>
          </motion.div>
        ) : (
          <div className="featured-empty">
            <p>No featured products available right now.</p>
          </div>
        )}
      </Container>
    </section>
  );
};

export default Featured;
